import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { Role, User } from '../types';
import { IS_ADMIN_SURFACE } from '../utils/surface';
import { blocksAdminSurfaceOverride } from '../utils/roles';

interface ProtectedRouteProps {
  children: React.ReactNode;
  check?: (role: Role, user?: User) => boolean;
}

const SLUG_MAP: Record<string, string> = {
  integrators: 'integrators',
  customers: 'customers',
  properties: 'properties',
  tenants: 'tenants',
  'web-users': 'web-users',
  guests: 'guests',
  'call-history': 'call-history',
  'mobile-users': 'mobile-users',
  intercoms: 'intercoms',
  cameras: 'cameras',
  'access-control': 'access-control',
  voice: 'voice',
  devices: 'devices',
  billing: 'billing',
  accessories: 'accessories',
  settings: 'settings',
  deliveries: 'deliveries',
  vendors: 'vendors',
  rules: 'rules',
};

function routeToPageSlug(pathname: string): string | null {
  const segment = pathname.split('/').filter(Boolean)[0];
  if (!segment) return null;
  return SLUG_MAP[segment] || null;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, check }) => {
  const { user, loading: authLoading } = useAuth();
  const { canView } = usePermissions();
  const location = useLocation();

  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // The admin shell's super-admin gate is authoritative. A page-level RBAC
  // row may broaden access inside the app surface, but it must never override
  // the admin surface boundary.
  if (blocksAdminSurfaceOverride(IS_ADMIN_SURFACE, user, check)) {
    return <Navigate to="/login" replace />;
  }

  const pageSlug = routeToPageSlug(location.pathname);

  if (pageSlug) {
    const rbacView = canView(pageSlug);

    // If RBAC has an explicit value, it overrides the hardcoded check
    if (rbacView !== undefined) {
      if (!rbacView) {
        return <Navigate to="/dashboard" replace />;
      }
      return <>{children}</>;
    }
  }

  // No RBAC override — fall back to hardcoded check
  if (check && !check(user.role, user)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};
